import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import CreateDiscount from '../components/CreateDiscount';
import DiscountCard from '../components/DiscountCard';

const Discounts = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [discounts, setDiscounts] = useState([]);
  const [adLoaded, setAdLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDiscounts, setTotalDiscounts] = useState(0);
  const ITEMS_PER_PAGE = 12;

  const platforms = [
    'All',
    'Steam',
    'Epic Games',
    'GOG',
    'Fanatical',
    'Green Man Gaming',
    'Humble Bundle',
    'PlayStation',
    'Xbox',
    'Nintendo',
    'Origin',
    'Ubisoft Store',
    'Battle.net',
    'itch.io',
    'Other'
  ];

  const fetchDiscounts = async (platform = 'All', page = 1) => {
    try {
      setLoading(true);
      setError('');
      const params = { 
        page, 
        limit: ITEMS_PER_PAGE 
      };
      if (platform !== 'All') {
        params.platform = platform;
      }
      const response = await axios.get('/api/discounts', { params });
      setDiscounts(response.data.discounts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalDiscounts(response.data.total || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch discounts');
      console.error('Error fetching discounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts(selectedPlatform, 1);
    setCurrentPage(1);
  }, [selectedPlatform]);

  const handlePlatformChange = (platform) => {
    setSelectedPlatform(platform);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchDiscounts(selectedPlatform, newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDiscountCreated = (newDiscount) => {
    // Refresh the discounts list to show the new discount
    fetchDiscounts(selectedPlatform, 1);
    setShowCreateForm(false);
  };

  const handleDeleteDiscount = async (discountId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/discounts/${discountId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Refresh the current page after deletion
      fetchDiscounts(selectedPlatform, currentPage);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete discount');
    }
  };

  return (
    <div className="discounts-page">
      {/* Header */}
      <div className="discounts-header">
        <div className="header-content">
          <h1>🎮 Game Discounts</h1>
          <p className="subtitle">
            Discover the best game deals shared by the community
          </p>
        </div>
        {isAuthenticated && (
          <button
            className="btn btn-primary btn-create"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ Post Discount'}
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <CreateDiscount onDiscountCreated={handleDiscountCreated} />
      )}

      {/* Platform Filter */}
      <div className="platform-filter">
        <div className="filter-label">Filter by platform:</div>
        <div className="platform-buttons">
          {platforms.map((platform) => (
            <button
              key={platform}
              className={`platform-btn ${
                selectedPlatform === platform ? 'active' : ''
              }`}
              onClick={() => handlePlatformChange(platform)}
            >
              {platform}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading discounts...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => fetchDiscounts(selectedPlatform, currentPage)} className="btn btn-secondary">
            Try Again
          </button>
        </div>
      )}

      {/* Discounts Grid */}
      {!loading && !error && (
        <>
          {discounts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎁</div>
              <h3>No discounts found</h3>
              <p>
                {selectedPlatform !== 'All'
                  ? `No active discounts for ${selectedPlatform} right now.`
                  : 'No active discounts at the moment.'}
              </p>
              {isAuthenticated && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Be the first to post a discount!
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="discounts-info">
                <p>Showing {discounts.length} of {totalDiscounts} discount{totalDiscounts !== 1 ? 's' : ''}</p>
              </div>
              <div className="discounts-grid">
                {discounts.map((discount, index) => (
                  <React.Fragment key={discount._id}>
                    <DiscountCard
                      discount={discount}
                      onDelete={handleDeleteDiscount}
                    />
                  </React.Fragment>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-pages">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return <span key={pageNum} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <style jsx>{`
        .discounts-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 24px;
        }

        .discounts-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .header-content h1 {
          font-size: 2.5em;
          margin: 0;
          color: var(--text-primary);
        }

        .subtitle {
          color: var(--text-secondary);
          margin: 8px 0 0 0;
          font-size: 1.1em;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 1em;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--btn-primary);
          color: white;
        }

        .btn-primary:hover {
          background: var(--btn-primary-hover);
        }

        .btn-secondary {
          background: var(--btn-secondary);
          color: white;
        }

        .btn-secondary:hover {
          background: var(--btn-secondary-hover);
        }

        .btn-create {
          padding: 14px 28px;
          font-size: 1.05em;
        }

        .platform-filter {
          background: var(--card-bg);
          padding: 20px;
          border-radius: 12px;
          box-shadow: var(--shadow);
          margin-bottom: 24px;
          border: 1px solid var(--border-color);
        }

        .ad-notice {
          padding: 20px;
          background: rgba(255, 193, 7, 0.1);
          border: 2px dashed rgba(255, 193, 7, 0.5);
          border-radius: 8px;
          color: #ffc107;
          font-weight: 500;
        }

        .ad-loading {
          padding: 20px;
          color: var(--text-secondary);
          font-style: italic;
        }

        .filter-label {
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .platform-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .platform-btn {
          padding: 8px 16px;
          border: 2px solid var(--border-color);
          background: var(--card-bg);
          border-radius: 20px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          color: var(--text-secondary);
        }

        .platform-btn:hover {
          border-color: var(--btn-primary);
          color: var(--btn-primary);
        }

        .platform-btn.active {
          background: var(--btn-primary);
          color: white;
          border-color: var(--btn-primary);
        }

        .loading-container {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-primary);
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--border-color);
          border-top: 4px solid var(--btn-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .error-container {
          text-align: center;
          padding: 60px 20px;
          background: var(--error-bg);
          border-radius: 12px;
          color: var(--error-text);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          background: var(--card-bg);
          border-radius: 12px;
          box-shadow: var(--shadow);
          border: 1px solid var(--border-color);
        }

        .empty-icon {
          font-size: 4em;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          color: var(--text-primary);
          font-size: 1.8em;
          margin: 0 0 12px 0;
        }

        .empty-state p {
          color: var(--text-secondary);
          font-size: 1.1em;
          margin: 0 0 24px 0;
        }

        .discounts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .discounts-info {
          text-align: center;
          color: var(--text-secondary);
          margin-bottom: 16px;
          font-size: 0.95em;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin: 40px 0;
          padding: 20px;
          background: var(--card-bg);
          border-radius: 12px;
          box-shadow: var(--shadow);
          border: 1px solid var(--border-color);
          flex-wrap: wrap;
        }

        .pagination-btn {
          padding: 10px 20px;
          border: 2px solid var(--border-color);
          background: var(--card-bg);
          color: var(--text-primary);
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
          min-width: 110px;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--btn-primary);
          background: var(--btn-primary);
          color: white;
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pagination-pages {
          display: flex;
          gap: 6px;
          align-items: center;
        }

        .pagination-page {
          padding: 8px 14px;
          border: 2px solid var(--border-color);
          background: var(--card-bg);
          color: var(--text-primary);
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          min-width: 40px;
        }

        .pagination-page:hover {
          border-color: var(--btn-primary);
          color: var(--btn-primary);
        }

        .pagination-page.active {
          background: var(--btn-primary);
          color: white;
          border-color: var(--btn-primary);
        }

        .pagination-ellipsis {
          color: var(--text-secondary);
          padding: 0 8px;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .discounts-page {
            padding: 16px;
          }

          .discounts-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-content h1 {
            font-size: 2em;
          }

          .discounts-grid {
            grid-template-columns: 1fr;
          }

          .pagination {
            padding: 12px;
          }

          .pagination-btn {
            min-width: 90px;
            padding: 8px 16px;
            font-size: 0.9em;
          }

          .pagination-page {
            padding: 6px 10px;
            min-width: 36px;
            font-size: 0.9em;
          }
        }
      `}</style>
    </div>
  );
};

export default Discounts;
